import { escapeHtml, formatElasticPriceHtml } from './helpers';

const productCardLightSwitcherMatchesCategories = (
    product,
    categoryIds = []
) => {
    if (!Array.isArray(categoryIds) || categoryIds.length === 0) {
        return true;
    }

    const enabledIds = new Set(
        categoryIds.map(id => Number(id)).filter(id => id > 0)
    );
    const terms = Array.isArray(product?.product_cat) ? product.product_cat : [];

    return terms.some(term => enabledIds.has(Number(term?.term_id || 0)));
};

const LISTING_COLOR_SWATCHES_VISIBLE = 3;

const renderProductCardColorSwatches = (variations = []) => {
    if (!Array.isArray(variations) || variations.length === 0) {
        return '';
    }

    const activeVariations = variations.filter(
        variation => variation?.is_active !== false
    );

    if (activeVariations.length === 0) {
        return '';
    }

    const visible = activeVariations.slice(0, LISTING_COLOR_SWATCHES_VISIBLE);
    const hiddenCount = Math.max(0, activeVariations.length - visible.length);

    const buttons = visible
        .map((variation, index) => {
            const label = escapeHtml(String(variation?.label || '').trim());
            const hex = escapeHtml(String(variation?.hex || '').trim());
            const imageUrl = escapeHtml(String(variation?.image_url || '').trim());
            const lightImageUrl = escapeHtml(
                String(variation?.light_image_url || '').trim()
            );
            const url = escapeHtml(String(variation?.url || '').trim());
            const slug = escapeHtml(String(variation?.slug || '').trim());
            const hasSelected = visible.some(
                row => row?.is_selected === true
            );
            const selectedClass =
                variation?.is_selected === true ||
                (!hasSelected && index === 0)
                    ? ' is-selected'
                    : '';

            if (slug === '') {
                return '';
            }

            return `<button type="button" js-product-card-color class="color-swatch product-card-color cursor-pointer${selectedClass}" data-image-url="${imageUrl}" data-light-image-url="${lightImageUrl}" data-url="${url}" data-slug="${slug}" aria-label="${label}" js-tippy="${label}"><span class="color-swatch__fill" style="background: ${hex};"></span></button>`;
        })
        .filter(markup => markup !== '')
        .join('');

    if (buttons === '') {
        return '';
    }

    const overflowMarkup =
        hiddenCount > 0
            ? `<span class="text-tiny font-semibold leading-none">+${hiddenCount}</span>`
            : '';

    return `<div class="flex flex-col items-center gap-4 pointer-events-auto" js-product-card-colors>${buttons}${overflowMarkup}</div>`;
};

/**
 * Client-side product card renderer for Elastic listing results.
 * Canonical PHP markup lives in template-parts/cards/card-product.php (SSR).
 */
export const renderProductCardFromElastic = (product, options = {}) => {
    const placeholderImageUrl = String(options.placeholderImageUrl || '');
    const seoTitle = escapeHtml(product.post_title || '');
    const displayName = escapeHtml(String(product.listing_display_name || '').trim());
    const categoryName = escapeHtml(product.primary_category_name || '');
    const permalink = escapeHtml(product.permalink || '#');
    const placeholder = escapeHtml(placeholderImageUrl);
    const thumbnails = Array.isArray(product.thumbnails)
        ? product.thumbnails
        : [];
    const listingColorVariations = Array.isArray(product.listing_color_variations)
        ? product.listing_color_variations
        : [];
    const mainImage = String(thumbnails[0]?.url || '').trim();
    const hoverImage = thumbnails[1]?.url || '';
    const priceHtml = formatElasticPriceHtml(product);
    const switcherEnabled = productCardLightSwitcherMatchesCategories(
        product,
        options.lightSwitcherCategoryIds
    );
    const hasHoverImage = switcherEnabled && hoverImage !== '';
    const mainImageSrc = escapeHtml(mainImage || placeholder);
    const colorSwatchesMarkup = renderProductCardColorSwatches(
        listingColorVariations
    );
    const cardPermalink = escapeHtml(
        String(product.permalink || '#').trim() || permalink
    );

    const imageAlt = displayName || seoTitle;

    const mainImageMarkup =
        mainImageSrc !== ''
            ? `<img class="js-product-card-main-image bg-product-background rounded-medium w-full h-full object-cover block group-[.-light]:opacity-0 transition-all" src="${mainImageSrc}" alt="${imageAlt}">`
            : '';

    const hoverImageMarkup = hasHoverImage
        ? `<img class="js-product-card-light-image absolute left-0 top-0 w-full h-full object-cover rounded-medium opacity-0 group-[.-light]:opacity-100 transition-all" src="${escapeHtml(
              hoverImage
          )}" alt="${imageAlt}">`
        : '';

    const switchMarkup = hasHoverImage
        ? `<button js-switch-image type="button" class="bg-beige-500 block h-6 w-[48px] desktop:w-[56px] cursor-pointer rounded-full relative p-0.5 pointer-events-auto"><div class="absolute left-0.5 top-1/2 h-5 -translate-y-1/2 w-5 bg-orange-500 origin-right rounded-full transition-all group-[.-light]:left-[calc(100%-14px)] group-[.-light]:w-3 group-[.-light]:rounded-bl-[100px] group-[.-light]:rounded-tl-[100px]"></div></button>`
        : '';

    const categoryMarkup =
        categoryName !== ''
            ? `<p class="text-tiny font-normal">${categoryName}</p>`
            : '';
    const priceMarkup =
        priceHtml !== ''
            ? `<p class="text-tiny font-normal">${priceHtml}</p>`
            : '';

    return `<div class="group relative" data-product-card><a class="block aspect-square rounded-medium overflow-hidden relative bg-product-background rounded-medium isolate js-product-card-link" href="${cardPermalink}" title="${displayName || seoTitle}">${mainImageMarkup}${hoverImageMarkup}<div class="absolute z-10 left-0 top-0 w-full h-full p-20 flex flex-col justify-between transition-all desktop:opacity-0 desktop:group-hover:opacity-100 pointer-events-none"><div class="flex justify-end">${switchMarkup}</div><div class="flex justify-between items-end text-text-primary group-[&.-light]:text-text-contrast"><div>${categoryMarkup}${displayName !== '' ? `<p class="text-regular font-semibold">${displayName}</p>` : ''}${priceMarkup}</div><div class="min-w-[28px]"></div></div></div></a>${colorSwatchesMarkup !== '' ? `<div class="absolute z-20 right-20 bottom-20 transition-all desktop:opacity-0 desktop:group-hover:opacity-100 pointer-events-none">${colorSwatchesMarkup}</div>` : ''}</div>`;
};

export const toggleProductCardImage = button => {
    const card = button.closest('[data-product-card]');

    if (!card) {
        return false;
    }

    card.classList.toggle('-light');

    return true;
};

export const selectProductCardColor = button => {
    const cardRoot = button.closest('[data-product-card]');

    if (!cardRoot) {
        return false;
    }

    const cardLink = cardRoot.querySelector('.js-product-card-link');

    if (!cardLink) {
        return false;
    }

    const imageUrl = String(button.dataset.imageUrl || '').trim();
    const lightImageUrl = String(button.dataset.lightImageUrl || '').trim();
    const variationUrl = String(button.dataset.url || '').trim();
    const mainImage = cardRoot.querySelector('.js-product-card-main-image');
    const lightImage = cardRoot.querySelector('.js-product-card-light-image');
    const swatches = cardRoot.querySelectorAll('[js-product-card-color]');

    swatches.forEach(swatch => {
        swatch.classList.toggle('is-selected', swatch === button);
    });

    if (mainImage instanceof HTMLImageElement && imageUrl !== '') {
        mainImage.src = imageUrl;
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
    }

    if (lightImage instanceof HTMLImageElement) {
        if (lightImageUrl !== '') {
            lightImage.src = lightImageUrl;
            lightImage.removeAttribute('srcset');
            lightImage.removeAttribute('sizes');
            lightImage.classList.remove('hidden');
        } else {
            lightImage.classList.add('hidden');
            cardRoot.classList.remove('-light');
        }
    }

    if (variationUrl !== '') {
        cardLink.href = variationUrl;
    }

    return true;
};

export const initProductCardSwitchers = (root = document) => {
    if (!root || typeof root.addEventListener !== 'function') {
        return;
    }

    root.addEventListener('click', event => {
        const colorButton = event.target.closest('[js-product-card-color]');

        if (colorButton && root.contains(colorButton)) {
            event.preventDefault();
            event.stopPropagation();
            selectProductCardColor(colorButton);

            return;
        }

        const button = event.target.closest('[js-switch-image]');

        if (!button || !root.contains(button)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        toggleProductCardImage(button);
    });
};
